import { useState } from 'react';
import { supabase, supabaseUrl, supabaseAnonKey } from '../commons/config/settings';

export interface CreateUserData {
  full_name: string;
  email: string;
  role: 'admin' | 'agent';
}

export interface CreateUserResult {
  success: boolean;
  user?: any;
  error?: string;
  invitationLink?: string;
}

export interface UpdateUserData {
  full_name: string;
  role: 'admin' | 'agent';
}

export const useUserManagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createUser = async (userData: CreateUserData): Promise<CreateUserResult> => {
    setLoading(true);
    setError(null);

    try {
      // Ottieni la sessione corrente
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Sessione non valida');
      }

      // Crea utente tramite Edge Function
      const response = await fetch(`${supabaseUrl}/functions/v1/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': supabaseAnonKey
        },
        body: JSON.stringify({
          full_name: userData.full_name,
          email: userData.email,
          role: userData.role
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Errore nella creazione dell'utente: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      return { 
        success: true, 
        user: result.user,
        invitationLink: result.invitationLink 
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Errore sconosciuto';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId: string, updates: UpdateUserData) => {
    setLoading(true);
    setError(null);

    try {
      // 1. Aggiorna profilo in public.profiles
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: updates.full_name,
          role: updates.role
        })
        .eq('id', userId);

      if (profileError) {
        throw new Error(`Errore nell'aggiornamento del profilo: ${profileError.message}`);
      }

      // 2. Aggiorna user_metadata in auth.users tramite Edge Function
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Sessione non valida');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/update-user-metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': supabaseAnonKey
        },
        body: JSON.stringify({
          userId,
          updates: {
            full_name: updates.full_name,
            role: updates.role
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Errore nell'aggiornamento metadata: ${errorData.error || response.statusText}`);
      }

      const authUpdateData = await response.json();
      console.log('Auth update result:', authUpdateData);

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Errore sconosciuto';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const sendPasswordReset = async (email: string) => {
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/user/reset-password`
      });

      if (error) {
        throw new Error(`Errore nell'invio email reset: ${error.message}`);
      }

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Errore sconosciuto';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const getUserById = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        throw new Error(`Errore nel recupero utente: ${error.message}`);
      }

      return { success: true, user: data };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Errore sconosciuto';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const syncUserMetadata = async (userId: string) => {
    try {
      // Ottieni i dati dal profilo
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('full_name, role')
        .eq('id', userId)
        .single();

      if (profileError) {
        throw new Error(`Errore nel recupero profilo: ${profileError.message}`);
      }

      // Sincronizza con auth.users tramite Edge Function
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Sessione non valida');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/update-user-metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': supabaseAnonKey
        },
        body: JSON.stringify({
          userId,
          updates: {
            full_name: profile.full_name,
            role: profile.role
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Errore nella sincronizzazione: ${errorData.error || response.statusText}`);
      }

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Errore sconosciuto';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  return {
    loading,
    error,
    createUser,
    updateUser,
    sendPasswordReset,
    getUserById,
    syncUserMetadata,
    clearError: () => setError(null)
  };
}; 